// src/components/Live.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Page from "./Page";
import LiveHeader from "./LiveHeader";
import TranscriptionPanel from "./TranscriptionPanel";
import CuePanel from "./CuePanel";
import "./components.css";
import { CueNote } from "../types";
import { Info } from "lucide-react";

import { useUiStore } from "../state/ui-store";
import { getNotesForMatcher } from "../state/notes-store";
import { useAudioTranscription } from "../hooks/useAudioTranscription";
import { useWatchdog } from "../hooks/useWatchdog";
import { useCueMatcher } from "../hooks/useCueMatcher";
import { useNumberHotkeys, useReshareHotkey } from "../hooks/useGlobalHotkeys";

import {
  DEBUG_MATCHER_LOGS,
  DEV_HOOKS,
  LIVE_HINT_KEYS,
} from "../config/live";
import { softRestartStreaming, startSystemCapture, stopStreaming } from "../audio/audio-client";

// ---------- Local hint utilities (unchanged behavior) ----------
function useResetHintsFromQuery(prefix: string) {
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset-hints") === "1") {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || "";
        if (k.startsWith(prefix)) keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      setRefresh((r) => r + 1);
    }
  }, [prefix]);
  return refresh;
}

function Hint({
  id,
  children,
  style,
  className,
  compact = false,
  refreshToken = 0,
}: {
  id: (typeof LIVE_HINT_KEYS)[number];
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  compact?: boolean;
  refreshToken?: number;
}) {
  const storageKey = `cueboard.hint.live.${id}`;
  const computeVisible = () => {
    try {
      return localStorage.getItem(storageKey) !== "dismissed";
    } catch {
      return true;
    }
  };
  const [visible, setVisible] = useState<boolean>(computeVisible);
  useEffect(() => { setVisible(computeVisible()); }, [refreshToken]);
  if (!visible) return null;

  const baseClasses = `hint ${compact ? "compact" : ""}`;
  return (
    <div className={`${baseClasses} ${className ?? ""}`} style={style} role="note" aria-live="polite">
      <Info size={16} className="hint-icon" aria-hidden />
      <div className="hint-content">{children}</div>
      <button
        type="button"
        aria-label="Dismiss hint"
        onClick={() => {
          try { localStorage.setItem(storageKey, "dismissed"); } catch {}
          setVisible(false);
        }}
        className="button button-ghost hint-dismiss"
      >
        ×
      </button>
    </div>
  );
}

// ---------- Component ----------
export default function Live() {
  // Notes + selection (kept local to preserve exact behavior)
  const [notes, setNotes] = useState<CueNote[]>([]);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);

  // Refs mirroring those states for hooks that need stable reads
  const notesRef = useRef(notes);
  const activeRef = useRef(activeNoteId);
  const pinnedRef = useRef(pinned);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { activeRef.current = activeNoteId; }, [activeNoteId]);
  useEffect(() => { pinnedRef.current = pinned; }, [pinned]);

  // Load notes once from localStorage (unchanged)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("cueboard.notes");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setNotes(parsed);
        }
      }
    } catch {}
  }, []);

  // Transcript + audio lifecycle (set handler BEFORE start)
  const {
    transcript,
    transcriptTail,
    shouldBeListening,
    listening,
    handleStart,
    handleStop,
    setListening, // used by watchdog to keep UI "active"
  } = useAudioTranscription({
    onPartial: (_text) => { watchdog.notifyPartial(); },
    onFinal: (text, origin) => {
      watchdog.notifyFinal();
      matcher.processFinal(text, origin);
    },
    onStreamEnd: () => { watchdog.notifyStreamEnd(); },
  });

  // Watchdog (UI-side orchestration only)
  const watchdog = useWatchdog({
    armed: shouldBeListening,
    onSoftRestart: async () => { await softRestartStreaming(); },
    setUiActive: setListening,
  });

  // Cue matcher (unchanged logic, moved into a hook)
  const matcher = useCueMatcher(
    useMemo(() => getNotesForMatcher(), [notes]), // matcher uses runtime-expanded notes
    {
      pinnedRef,
      activeRef,
      onHit: (id: string) => setActiveNoteId(id),
      debug: DEBUG_MATCHER_LOGS,
    }
  );

  // Trigger tester bridge (unchanged)
  useEffect(() => {
    function onTest(e: Event) {
      const ev = e as CustomEvent<{ text: string }>;
      const text = (ev.detail?.text ?? "").trim();
      if (!text) return;
      matcher.processFinal(text, "system");
    }
    window.addEventListener("cueboard:test-trigger", onTest as EventListener);
    return () => window.removeEventListener("cueboard:test-trigger", onTest as EventListener);
  }, [matcher]);

  // Number hotkeys 1–9 (unchanged behavior)
  useNumberHotkeys(notesRef, (id) => setActiveNoteId(id));

  // Reshare button and hotkey
  const reshareBtnRef = useRef<HTMLButtonElement | null>(null);
  const reshareInFlight = useRef<boolean>(false);

  const notifyUser = (msg: string) => { try { window.alert(msg); } catch {} };

  const doReshare = async () => {
    if (reshareInFlight.current) return;
    reshareInFlight.current = true;
    try {
      try { await stopStreaming({ preserveCapture: false } as any); } catch {}
      await startSystemCapture(); // user gesture → browser shows picker
      const t = Date.now();
      // re-seed watchdog ages
      watchdog.notifyStreamStart();
      (window as any).cueboard?.wdSetAges?.({ sessionMsAgo: 0, partialMsAgo: 0, finalMsAgo: 0 }); // no-op if not DEV
      setListening(true); // keep UI active
      // eslint-disable-next-line no-console
      console.log("[LIVE] Manual re-share succeeded.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[LIVE] Manual re-share failed", err);
      notifyUser(
        "Re-share was blocked by the browser. Click the 'Re-share System Audio' button and, in the prompt, select a screen/tab and enable 'Share system audio'."
      );
      try { reshareBtnRef.current?.focus(); } catch {}
    } finally {
      reshareInFlight.current = false;
    }
  };

  // Hotkey: 'R' reshare if last heard ≥ 7s; DEV Ctrl+Alt+R to force watchdog
  const shouldRef = useRef(false);
  useEffect(() => { shouldRef.current = shouldBeListening; }, [shouldBeListening]);

  useReshareHotkey(
    shouldRef,
    () => watchdog.lastHeardAgoSec,
    () => { void doReshare(); },
    DEV_HOOKS ? () => { (window as any).cueboard?.wdForceOnce?.(); } : undefined
  );

  // Sidebar: Presentation toggle (unchanged)
  const stealth = useUiStore((s) => s.stealth);

  // Hints
  const resetRefreshToken = useResetHintsFromQuery("cueboard.hint.live.");
  const [hintRefreshToken, setHintRefreshToken] = useState(0);
  const combinedRefreshToken = resetRefreshToken + hintRefreshToken;

  const [hintsHidden, setHintsHidden] = useState<boolean>(() => {
    try {
      return LIVE_HINT_KEYS.every((k) => localStorage.getItem(`cueboard.hint.live.${k}`) === "dismissed");
    } catch {
      return false;
    }
  });

  const setAllHints = (state: "dismissed" | null) => {
    LIVE_HINT_KEYS.forEach((k) => {
      const key = `cueboard.hint.live.${k}`;
      try {
        if (state === "dismissed") localStorage.setItem(key, "dismissed");
        else localStorage.removeItem(key);
      } catch {}
    });
    setHintRefreshToken((t) => t + 1);
  };

  const toggleHints = () => {
    if (hintsHidden) {
      setAllHints(null);
      setHintsHidden(false);
    } else {
      setAllHints("dismissed");
      setHintsHidden(true);
    }
  };

  // Prev/Next (unchanged)
  const currentIdx = activeNoteId ? notes.findIndex((n) => n.id === activeNoteId) : -1;
  const onPrev = () => {
    if (!notes.length) return;
    const idx = currentIdx < 0 ? 0 : currentIdx;
    const prevIdx = (idx - 1 + notes.length) % notes.length;
    setActiveNoteId(notes[prevIdx].id);
  };
  const onNext = () => {
    if (!notes.length) return;
    if (activeNoteId === null) {
      setActiveNoteId(notes[0].id);
      return;
    }
    const idx = notes.findIndex((n) => n.id === activeNoteId);
    const nextIdx = (idx + 1) % notes.length;
    setActiveNoteId(notes[nextIdx].id);
  };

  const activeNote = notes.find((n) => n.id === activeNoteId) || null;

  // UI computed bits (match original)
  const listeningSinceMs = watchdog.listeningSinceMs || 0;
  const lastHeardAgoSec = shouldBeListening ? watchdog.lastHeardAgoSec : null;
  const showReshare = shouldBeListening && lastHeardAgoSec !== null && lastHeardAgoSec >= 7;

  return (
    <Page>
      {/* Header — mirror Preview layout exactly */}
      <LiveHeader
        shouldBeListening={shouldBeListening}
        listeningSinceMs={listeningSinceMs}
        lastHeardAgoSec={lastHeardAgoSec}
        rightControls={
          <button
            className="button button-ghost"
            style={{ fontSize: 12, padding: "2px 6px" }}
            onClick={toggleHints}
            title={hintsHidden ? "Show Live hints again" : "Hide Live hints"}
          >
            {hintsHidden ? "Unhide Live Hints" : "Hide Live Hints"}
          </button>
        }
      />

      <div className={`live-page ${stealth ? "stealth-on" : ""}`}>
        <div className="live-container">

          {/* Left: Transcription pane */}
          <TranscriptionPanel
            shouldBeListening={shouldBeListening}
            onStart={() => {
              watchdog.notifyStreamStart(); // seed ages before capture starts
              handleStart();
            }}
            onStop={handleStop}
            onReshare={() => { void doReshare(); }}
            reshareBtnRef={reshareBtnRef}
            transcript={transcript}
            transcriptTail={transcriptTail}
            prefixControls={
              <>
                <Hint id="startshare" compact style={{ marginBottom: 8 }} refreshToken={combinedRefreshToken}>
                  <b>Before your call</b> — Click <b>Start</b>, wait for the <b>picker</b> to appear, then choose the screen or tab you’ll share. Make sure <b>'Also share system audio'</b> is enabled when prompted. <i>Do this <b>before</b> you join so CueBoard is listening and ready.</i>
                  <div className="muted small" style={{ marginTop: 4 }}>
                      <br /><br />
Safe to share: This won’t interfere with Zoom/Meet — you can still share slides or other screens in your call. Avoid double-sharing system audio.
                  </div>
                </Hint>

                <Hint id="systemonly" compact style={{ marginTop: 8, marginBottom: 8 }} refreshToken={combinedRefreshToken}>
                  <b>System audio</b> is required for triggers. <i>Mic mode never triggers cues by design.</i>
                                  </Hint>
              </>
            }
          />

          {/* Right: Cue panel */}
          <div className="card sidebar-card cueboard-pane vstack-sm">
            <CuePanel
              stealth={stealth}
              onToggleStealth={() => {
                const next = !stealth;
                useUiStore.getState().toggleStealth();
                try { localStorage.setItem("cueboard.stealth", next.toString()); } catch {}
              }}
              pinned={pinned}
              onTogglePin={() => setPinned((p) => !p)}
              onPrev={onPrev}
              onNext={onNext}
              activeNote={activeNote}
              activeIndex={activeNote ? notes.findIndex((n) => n.id === activeNote.id) : -1}
            />

            {/* Preserve the two hints below the controls as in original */}
            <Hint id="presentation" compact style={{ marginBottom: 8 }} refreshToken={combinedRefreshToken}>
              <b>Presentation Mode</b> blurs the side panel.
            </Hint>

            <Hint id="pinhotkeys" compact style={{ marginBottom: 8 }} refreshToken={combinedRefreshToken}>
              <b>Pin</b> keeps a CueBoard visible even if a new trigger fires. <i>Press <b>1–9</b> to jump between boards instantly, or <b>Previous/Next</b> to step.</i>
            </Hint>
          </div>
        </div>
      </div>
    </Page>
  );
}
